/**
 * useFinanceiroVehicles - Vínculo entre documentos financeiros e veículos
 *
 * Toda leitura/escrita passa por RPCs SECURITY DEFINER:
 *  - list_financeiro_vehicles : veículos de uma cobrança OU assinatura
 *                               (ciclo de assinatura herda os veículos da mãe)
 *  - set_financeiro_vehicles  : substitui o conjunto de veículos do documento
 *  - get_vehicle_financeiro   : cobranças/assinaturas de um veículo (aba Financeiro)
 *
 * @version 1.2.0
 * RELEASE: 2026-08-18 v1.2.0 - Campos de proprietário da época (owner_customer_id/owner_name)
 *                              vindos do ledger de posse (vehicle_ownerships).
 * RELEASE: 2026-08-18 v1.0.0 - Criação inicial (Financeiro x Veículos)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useFinanceiroVehicles');

/** Veículo vinculado a um documento financeiro */
export interface FinanceiroVehicle {
  vehicle_id: string;
  plate: string | null;
  brand_name: string | null;
  model_name: string | null;
  year: number | null;
  color: string | null;
  /** true quando o vínculo vem da assinatura mãe (ciclo herdado) */
  inherited: boolean;
  /** v1.2.0 — proprietário carimbado no vínculo (dono na data da emissão) */
  owner_customer_id?: string | null;
  owner_name?: string | null;
}

/** Cobrança exibida na aba Financeiro do veículo */
export interface VehicleCobranca {
  id: string;
  descricao: string | null;
  valor_total: number;
  status: string;
  tipo_cobranca: string;
  data_vencimento: string;
  assinatura_id: string | null;
  ciclo_numero: number | null;
  /** v1.2.0 — preenchido apenas no bloco de histórico */
  customer_id?: string | null;
  owner_name?: string | null;
}

/** Assinatura exibida na aba Financeiro do veículo */
export interface VehicleAssinatura {
  id: string;
  descricao: string | null;
  valor_total: number;
  status: string;
  frequencia: string;
  proximo_vencimento: string | null;
  /** v1.2.0 — preenchido apenas no bloco de histórico */
  customer_id?: string | null;
  owner_name?: string | null;
}

/** Período de posse do veículo (ledger SCD Tipo 2) */
export interface VehicleOwnershipPeriod {
  ownership_id: string;
  customer_id: string | null;
  owner_name: string | null;
  valid_from: string;
  valid_to: string | null;
}

/** Retorno consolidado da aba Financeiro do veículo */
export interface VehicleFinanceiro {
  cobrancas: VehicleCobranca[];
  assinaturas: VehicleAssinatura[];
  total_aberto: number;
  total_pago: number;
  /** v1.2.0 — proprietário vigente segundo o ledger de posse */
  owner_customer_id?: string | null;
  /**
   * v1.1.0 — documentos que pertencem a proprietários anteriores do veículo
   * (gerados antes de uma transferência). Ficam fora dos KPIs principais.
   */
  historico_outros_clientes?: {
    cobrancas: VehicleCobranca[];
    assinaturas: VehicleAssinatura[];
    total: number;
  };
  /** v1.2.0 — linha do tempo de proprietários */
  periodos_posse?: VehicleOwnershipPeriod[];
}


/** Alvo do vínculo: exatamente um dos dois deve ser informado */
interface FinanceiroVehicleTarget {
  cobrancaId?: string | null;
  assinaturaId?: string | null;
}

/**
 * Lista os veículos vinculados a uma cobrança ou assinatura.
 */
export function useFinanceiroVehicles(
  target: FinanceiroVehicleTarget,
  enabled = true,
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const hasTarget = !!(target.cobrancaId || target.assinaturaId);

  return useQuery({
    queryKey: [
      'financeiro-vehicles',
      tenantId,
      target.cobrancaId ?? null,
      target.assinaturaId ?? null,
    ],
    enabled: enabled && hasTarget && !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<FinanceiroVehicle[]> => {
      const { data, error } = await (supabase.rpc as any)('list_financeiro_vehicles', {
        p_cobranca_id: target.cobrancaId ?? null,
        p_assinatura_id: target.assinaturaId ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar veículos do documento financeiro', {
          error: error.message,
        });
        throw error;
      }
      return (data || []) as FinanceiroVehicle[];
    },
  });
}

/**
 * Define (substitui) os veículos vinculados a uma cobrança ou assinatura.
 */
export function useSetFinanceiroVehicles() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (
      params: FinanceiroVehicleTarget & { vehicleIds: string[] },
    ) => {
      const { data, error } = await (supabase.rpc as any)('set_financeiro_vehicles', {
        p_vehicle_ids: params.vehicleIds,
        p_cobranca_id: params.cobrancaId ?? null,
        p_assinatura_id: params.assinaturaId ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao vincular veículos ao financeiro', { error: error.message });
        throw error;
      }
      return data as number;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-financeiro'] });
      toast.success('Veículos vinculados atualizados');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao vincular veículos: ${error.message}`);
    },
  });
}

/**
 * Dados financeiros de um veículo (aba Financeiro do modal de veículo).
 */
export function useVehicleFinanceiro(vehicleId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['vehicle-financeiro', tenantId, vehicleId],
    enabled: enabled && !!vehicleId && !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<VehicleFinanceiro> => {
      const { data, error } = await (supabase.rpc as any)('get_vehicle_financeiro', {
        p_vehicle_id: vehicleId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar financeiro do veículo', { error: error.message });
        throw error;
      }
      return (data || {
        cobrancas: [],
        assinaturas: [],
        total_aberto: 0,
        total_pago: 0,
      }) as VehicleFinanceiro;
    },
  });
}
